'use client';

import { useMemo } from 'react';
import useSWR from 'swr';
import dayjs from 'dayjs';
import { myPlannerApi } from './my-planner.api';
import type { PlannerCounselSlotResponse } from './planner-counsel-slot.api';
import { getServiceRegionsByIds } from '@/entities/order/model/service-region.client';
import type { WorkScheduleDayData } from '@/entities/planner/ui';

const VALID_STATUSES = new Set(['APPOINTED', 'AVAILABLE']);

function getRegionDisplayName(region: {
  codePath?: string[];
  name?: string;
  code?: string;
  id: string;
}): string {
  if (region.codePath && region.codePath.length > 0) {
    return region.codePath.join('-');
  }
  return region.name || region.code || '알 수 없음';
}

async function fetchMyWorkScheduleCalendarData(
  month: string
): Promise<Record<string, WorkScheduleDayData>> {
  const m = dayjs(month, 'YYYY-MM');
  const startDate = m.startOf('month').format('YYYY-MM-DD');
  const endDate = m.endOf('month').format('YYYY-MM-DD');

  const slots = await myPlannerApi.getMySlotsByDateRange(startDate, endDate);

  const regionIds = Array.from(
    new Set(
      (slots as PlannerCounselSlotResponse[])
        .map((s) => s.regionId)
        .filter((id): id is string => !!id)
    )
  );

  const serviceRegions = await getServiceRegionsByIds(regionIds);
  const regionDisplayMap = new Map(serviceRegions.map((r) => [r.id, getRegionDisplayName(r)]));

  const result: Record<string, WorkScheduleDayData> = {};

  for (const slot of slots as PlannerCounselSlotResponse[]) {
    if (!slot.date) continue;

    if (!result[slot.date]) {
      result[slot.date] = { regions: [], regionIds: [], counsels: 0 };
    }

    const hasValidSlot = slot.slots?.some((s) => VALID_STATUSES.has(s.status));
    if (hasValidSlot && slot.regionId) {
      const regionName = regionDisplayMap.get(slot.regionId) ?? slot.regionId;
      if (!result[slot.date]!.regions.includes(regionName)) {
        result[slot.date]!.regions.push(regionName);
      }
      if (!result[slot.date]!.regionIds!.includes(slot.regionId)) {
        result[slot.date]!.regionIds!.push(slot.regionId);
      }
    }

    const appointedCount = slot.slots?.filter((s) => s.status === 'APPOINTED').length ?? 0;
    result[slot.date]!.counsels += appointedCount;
  }

  return result;
}

export interface UseMyPlannerWorkScheduleCalendarDataOptions {
  month: string;
  enabled?: boolean;
}

/**
 * PLANNER 전용: 근무표 캘린더용 데이터 조회
 * /api/v1/planners/me/visit-counsels/slots 사용
 */
export function useMyPlannerWorkScheduleCalendarData({
  month,
  enabled = true,
}: UseMyPlannerWorkScheduleCalendarDataOptions): {
  scheduleData: Record<string, WorkScheduleDayData>;
  isLoading: boolean;
  error: Error | null;
} {
  const swrKey = useMemo(
    () => (enabled && month ? (['my-planner-work-schedule-calendar', month] as const) : null),
    [enabled, month]
  );

  const { data, error, isLoading } = useSWR<Record<string, WorkScheduleDayData>>(
    swrKey,
    swrKey ? () => fetchMyWorkScheduleCalendarData(month) : null,
    { revalidateOnMount: true }
  );

  return {
    scheduleData: data ?? {},
    isLoading,
    error: error ? (error instanceof Error ? error : new Error(String(error))) : null,
  };
}
